/**
 * Pure audio math for the realtime path: browser float blocks in, provider PCM16/16k frames out.
 *
 * Everything here is a value transformation — no browser API, no state, no retention — so the whole
 * realtime pipeline's correctness reduces to functions a unit test can hold still.
 */

export const TARGET_SAMPLE_RATE = 16000;

/**
 * Box-filtered linear resample to mono 16k. Speech-adequate: each output sample averages the input
 * span it covers, which suppresses the aliasing a bare decimation would fold into the band.
 */
export function downsampledMono(samples: Float32Array, fromRate: number): Float32Array {
  if (fromRate === TARGET_SAMPLE_RATE) return samples;
  if (fromRate < TARGET_SAMPLE_RATE || samples.length === 0) {
    // Upsampling has no use here: the provider wants 16k and every real capture rate is higher.
    return samples;
  }
  const ratio = fromRate / TARGET_SAMPLE_RATE;
  const outLength = Math.floor(samples.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.min(samples.length, Math.max(start + 1, Math.floor((i + 1) * ratio)));
    let sum = 0;
    for (let j = start; j < end; j += 1) sum += samples[j] ?? 0;
    out[i] = sum / (end - start);
  }
  return out;
}

/** Clamped float→Int16, the provider's wire format. */
export function int16From(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i] ?? 0));
    out[i] = Math.round(clamped * 32767);
  }
  return out;
}

/** Little-endian bytes of the PCM, base64 for the JSON frame. */
export function base64FromInt16(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.length * 2);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < pcm.length; i += 1) view.setInt16(i * 2, pcm[i] ?? 0, true);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Unshaped signal energy. Voice-activity decisions live on this scale, where room noise stays low. */
export function rawRms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const value of samples) sum += value * value;
  return Math.sqrt(sum / samples.length);
}

/** Real microphone loudness for the UI; perceptually shaped so quiet speech still visibly moves. */
export function levelFromFloat(samples: Float32Array): number {
  return Math.min(1, Math.sqrt(rawRms(samples)) * 1.6);
}
